import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Button } from '@/shared/ui/Button'
import { Avatar } from '@/shared/ui/Avatar'
import { Spinner } from '@/shared/ui/Spinner'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { toast } from '@/store/useToastStore'
import { useUsers, useDeleteUser, useGearEnum } from '@/api/hooks'
import { useDebounce, useSessionState, useScrollRestoration } from '@/shared/hooks'
import { userService } from '@/api/services/userService'
import { schoolService } from '@/api/services/schoolService'
import { TrainerCard, mapSchoolTrainerRow } from './TrainerCard'
import { SendNotificationModal, type NotificationRecipient } from './SendNotificationModal'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  Plus, Search, SlidersHorizontal, Calendar, MapPin,
  Phone, Mail, ChevronLeft, ChevronRight, ChevronDown, Trash2, X, BadgeCheck, RotateCcw, Send, UserCog,
  ClipboardList, Package, Layers, Wallet, Coins, CreditCard,
} from 'lucide-react'
import { cn, DUMMY_AVATAR, onAvatarError, pickTranslation, localized } from '@/shared/utils'
import { getPaymentMethodLabel } from '@/shared/constants'
import { Can } from '@/shared/ui/Can'

// ── Types ──────────────────────────────────────────────────────────────────
const USER_TABS = [
  { value: 'ALL',            label: 'All'             },
  { value: 'STUDENT',        label: 'Student'         },
  { value: 'TRAINER',        label: 'Trainer'         },
  { value: 'ASSISTANT',      label: 'Assistant'       },
  { value: 'SCHOOL_ADMIN',   label: 'School Admin'    },
  { value: 'DELETED',        label: 'Deleted User'    },
] as const

type TabValue = typeof USER_TABS[number]['value']

// sessionStorage key holding the id of the user whose card was last opened, so
// back-navigation can scroll straight back to it instead of the top of the list.
const FOCUS_KEY = 'list-focus:user-list'

// ── User card ────────────────────────────────────────────────────────────
function formatDateTime(value?: string | null) {
  if (!value) return '—'
  // The API sends "2026-09-20 22:42:53" — a space, not the "T" ISO parsing
  // needs — so `new Date(value)` as-is returns Invalid Date in Safari/Firefox.
  const d = new Date(String(value).replace(' ', 'T'))
  if (isNaN(d.getTime())) return '—'
  return `${d.toLocaleDateString('en-GB').replace(/\//g,'-')} ${d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',hour12:true})}`
}

function statusLabelCls(label?: string | null) {
  const u = (label ?? '').toUpperCase().replace(/_/g, ' ')
  if (u === 'PENDING')      return 'bg-brand-50 text-brand-600'
  if (u === 'CONFIRMED')    return 'bg-blue-50 text-blue-600'
  if (u === 'IN PROGRESS')  return 'bg-purple-50 text-purple-600'
  if (u === 'COMPLETED')    return 'bg-emerald-50 text-emerald-600'
  if (u === 'CANCELLED')    return 'bg-red-50 text-red-600'
  return 'bg-gray-100 text-gray-500'
}
function paymentStatusCls(label?: string | null) {
  const u = (label ?? '').toUpperCase().replace(/_/g, ' ')
  if (u === 'PAID')            return 'bg-emerald-50 text-emerald-600'
  if (u === 'PENDING')         return 'bg-brand-50 text-brand-600'
  if (u === 'FAILED')          return 'bg-red-50 text-red-600'
  if (u === 'REFUNDED')        return 'bg-purple-50 text-purple-600'
  if (u === 'PARTIALLY PAID')  return 'bg-amber-50 text-amber-600'
  return 'bg-gray-100 text-gray-500'
}
// payment_status 0 ("pending") reads as "awaiting an admin action" in most of this
// app, but for a payment it just means the student hasn't paid yet — "Not Paid"
// says that plainly instead of the ambiguous raw "PENDING" label.
function paymentStatusText(t: (k: string) => string, status: number | undefined, label?: string | null) {
  if (status === 0 || (label ?? '').toUpperCase() === 'PENDING') return t('userList.notPaid')
  return String(label ?? '').replace(/_/g, ' ')
}

function UserCard({ user, onView, onDelete, onRestore, deleted, restoring, deleting, showType }: { user: any; onView: ()=>void; onDelete: ()=>void; onRestore?: ()=>void; deleted?: boolean; restoring?: boolean; deleting?: boolean; showType?: boolean }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const services: any[] = Array.isArray(user.services) ? user.services : []
  const userName = localized(user, 'name', i18n.language)
  // No phone, only an email — on this app that only happens via a Google/Gmail
  // sign-in (phone is otherwise required), so flag it rather than leave a blank row.
  const isGmailLogin = !!user.email && !user.phone

  const cityName = user.city?.name ?? user.user_preference?.city?.city_translations?.[0]?.name

  const bookingsCount = user.bookings_count ?? user.bookings?.length ?? 0
  const servicesCount = user.services_count ?? user.services?.length ?? 0
  const allInOneCount = user.all_in_one_count ?? user.all_in_one?.length ?? 0

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* ── Header ── */}
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative flex-shrink-0">
            <img src={user.photo_url || DUMMY_AVATAR} alt={userName} onError={onAvatarError}
              className="w-12 h-12 rounded-full object-cover bg-gray-100"/>
          </div>
          <div className="min-w-0">
            <div className="font-bold text-gray-900 text-sm leading-tight truncate">{userName}</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-xs text-gray-400">{t('userList.idPrefix')}{user.id?.toString().padStart(4,'0')}</span>
              {showType && user.user_type && (
                <span className="text-[10px] font-bold uppercase tracking-wide text-brand-600 bg-brand-50 rounded-full px-1.5 py-0.5">
                  {t(`userList.tabs.${user.user_type}`, String(user.user_type))}
                </span>
              )}
            </div>
          </div>
        </div>
        {user.wallet_balance != null && (
          <div className="text-end flex-shrink-0">
            <div className="text-sm font-black text-gray-900 whitespace-nowrap">
              {Number(user.wallet_balance).toLocaleString()} <span className="text-[10px] font-semibold text-gray-400">{t('userList.currency')}</span>
            </div>
            <div className="flex items-center justify-end gap-1 text-[10px] text-gray-400">
              <Wallet size={9}/>{t('userList.wallet')}
              {user.wallet_points > 0 && (
                <span className="flex items-center gap-0.5 text-amber-500"><Coins size={9}/>{user.wallet_points}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Contact info ── */}
      <div className="px-4 pb-3 space-y-1">
        {user.email && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Mail size={11} className="flex-shrink-0 text-gray-400"/>
            <span className="truncate">{user.email}</span>
            {isGmailLogin && (
              <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-bold uppercase tracking-wide">
                {t('userList.gmailLogin')}
              </span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Calendar size={11} className="flex-shrink-0 text-gray-400"/>
            <span>{t('userList.joined')}: {formatDateTime(user.created_at)}</span>
          </div>
          {user.phone && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Phone size={11} className="flex-shrink-0 text-gray-400"/>
              <span>{user.phone}</span>
            </div>
          )}
        </div>
        {cityName && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <MapPin size={11} className="flex-shrink-0 text-gray-400"/>
            <span>{cityName}</span>
          </div>
        )}
        {Array.isArray(user.handled_by) && user.handled_by.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <UserCog size={11} className="flex-shrink-0 text-gray-400"/>
            <span className="truncate">{t('userList.handledBy')}: {user.handled_by.map((h: any) => h.name).filter(Boolean).join(', ')}</span>
          </div>
        )}
        {deleted && (
          <>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Calendar size={11} className="flex-shrink-0 text-gray-400"/>
              <span>{t('userList.deletedAt')}: {formatDateTime(user.deleted_at)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Trash2 size={11} className="flex-shrink-0 text-gray-400"/>
              <span>{t('userList.deletedBy')}: {(user.deleted_by_user ?? user.deleted_by)?.name ?? t('userList.unknown')}</span>
            </div>
          </>
        )}
      </div>

      {/* ── Activity counts — each tag jumps to the relevant list, filtered to this user ── */}
      {(bookingsCount > 0 || servicesCount > 0 || allInOneCount > 0) && (
        <div className="px-4 pb-3 flex flex-wrap gap-1.5">
          {bookingsCount > 0 && (
            <button onClick={e => { e.stopPropagation(); navigate(`/dashboard/user/${user.id}?tab=booking`) }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-[11px] font-semibold hover:bg-blue-100 transition-colors">
              <ClipboardList size={10}/>{t('userList.bookingsCount', { count: bookingsCount })}
            </button>
          )}
          {servicesCount > 0 && (
            <button onClick={e => { e.stopPropagation(); navigate(`/dashboard/services?search=${encodeURIComponent(userName || user.phone || '')}`) }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 text-[11px] font-semibold hover:bg-purple-100 transition-colors">
              <Package size={10}/>{t('userList.servicesCount', { count: servicesCount })}
            </button>
          )}
          {allInOneCount > 0 && (
            <button onClick={e => { e.stopPropagation(); navigate(`/dashboard/all-in-one/enrollments?user_id=${user.id}`) }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[11px] font-semibold hover:bg-emerald-100 transition-colors">
              <Layers size={10}/>{t('userList.allInOneCount', { count: allInOneCount })}
            </button>
          )}
        </div>
      )}

      {/* ── Services — every registration, not just the latest ── */}
      {services.length > 0 && (
        <div className="mx-4 mb-3 rounded-xl overflow-hidden border border-gray-100">
          <button onClick={e => { e.stopPropagation(); navigate(`/dashboard/services?search=${encodeURIComponent(userName || user.phone || '')}`) }}
            className="w-full flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-100 hover:bg-gray-100 transition-colors">
            <span className="text-xs font-bold text-gray-600">{t('userList.servicesCount', { count: services.length })}</span>
            <ChevronRight size={12} className="text-gray-400 rtl:rotate-180"/>
          </button>
          <div className="divide-y divide-gray-50">
            {services.map((s: any, i: number) => (
              <div key={s.registration_id ?? i} className="px-3 py-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-6 h-6 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <Package size={12} className="text-purple-500"/>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-gray-800 truncate">{s.service_name}</div>
                    <div className="text-[10px] text-gray-400">{formatDateTime(s.created_at)}</div>
                    {s.payment_method != null && (
                      <div className="flex items-center gap-1 text-[10px] text-gray-400">
                        <CreditCard size={9} className="flex-shrink-0"/>{getPaymentMethodLabel(s.payment_method)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <div className="flex items-center gap-1">
                    {s.status_label && (
                      <span className={cn('px-1.5 py-0.5 rounded-full text-[9px] font-bold', statusLabelCls(s.status_label))}>
                        {String(s.status_label).replace(/_/g, ' ')}
                      </span>
                    )}
                    {s.payment_status_label && (
                      <span className={cn('px-1.5 py-0.5 rounded-full text-[9px] font-bold', paymentStatusCls(s.payment_status_label))}>
                        {paymentStatusText(t, s.payment_status, s.payment_status_label)}
                      </span>
                    )}
                  </div>
                  {s.amount_paid != null && (
                    <span className="text-xs font-bold text-gray-900">
                      {t('userList.currency')} {Number(s.amount_paid).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Actions ── */}
      <div className="px-4 pb-4 flex items-center gap-2">
        <button
          onClick={onView}
          className="flex-1 py-2 rounded-xl border-2 text-sm font-semibold transition-all hover:opacity-80"
          style={{ borderColor: '#f68b2c', color: '#f68b2c' }}
        >
          ● {t('userList.viewDetails')}
        </button>
        {deleted ? (
          <button
            onClick={onRestore}
            disabled={restoring}
            title={t('userList.restoreTitle')}
            className="flex items-center justify-center gap-1 py-2 px-3 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 bg-green-600 text-white hover:bg-green-700"
          >
            {restoring ? <Spinner className="w-4 h-4"/> : <RotateCcw size={16}/>}
            {t('userList.restore')}
          </button>
        ) : (
          <Can perm="DELETE /admin/user/delete-user-by-id">
          <button
            onClick={onDelete}
            disabled={deleting}
            title={t('userList.deleteTitle')}
            className="flex items-center justify-center py-2 px-3 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-colors disabled:opacity-50"
          >
            {deleting ? <Spinner className="w-4 h-4"/> : <Trash2 size={16}/>}
          </button>
          </Can>
        )}
      </div>
    </div>
  )
}

// ── Filter select — labelled, RTL-safe, highlights when a value is set ─────
function FilterSelect({ label, value, onChange, children }: {
  label: string; value: string; onChange: (v: string) => void; children: ReactNode
}) {
  return (
    <label className="flex flex-col gap-1.5 min-w-0">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          className={cn(
            'peer w-full h-10 appearance-none rounded-xl border ps-3 pe-9 text-sm truncate cursor-pointer',
            'transition-colors focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30',
            value
              ? 'border-brand-300 bg-brand-50/60 text-gray-900 font-medium'
              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
          )}
        >
          {children}
        </select>
        <ChevronDown
          size={14}
          className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 peer-focus:text-brand-500"
        />
      </div>
    </label>
  )
}

// ── Active filter chip ────────────────────────────────────────────────────
function FilterChip({ label, value, onRemove, removeLabel }: {
  label: string; value: string; onRemove: () => void; removeLabel: string
}) {
  return (
    <span className="inline-flex items-center gap-1.5 h-7 ps-2.5 pe-1 rounded-full text-xs bg-brand-50 text-brand-700 border border-brand-200 max-w-full">
      <span className="text-brand-500/80">{label}</span>
      <span className="font-semibold truncate max-w-[10rem]">{value}</span>
      <button
        onClick={onRemove}
        aria-label={removeLabel}
        className="p-0.5 rounded-full text-brand-400 hover:bg-brand-200/60 hover:text-brand-800 transition-colors"
      >
        <X size={11}/>
      </button>
    </span>
  )
}

// ── Gear value mapping (matches old gearTypeMapping exactly) ──────────────────
const GEAR_TYPE_MAP: Record<string, number> = { BOTH: 0, AUTOMATIC: 1, MANUAL: 2 }

// ── MAIN PAGE ──────────────────────────────────────────────────────────────
export function UserListPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const isSchool = location.pathname.startsWith('/school')

  // ── Filter state ──────────────────────────────────────────────────────────
  // Tab + filters persist for the same reason pagination does: a list -> details -> back
  // trip remounts this page, and a reset would drop the user on a different list than
  // the one they left.
  // School admins only ever see their own school's trainers — lock the tab to TRAINER.
  const [storedTab,    setTab]      = useSessionState<TabValue>('user-list:tab', 'ALL')
  const tab = isSchool ? 'TRAINER' : storedTab
  const [search,       setSearch]   = useSessionState('user-list:search', '')
  const [statusFilter, setStatus]   = useSessionState('user-list:status', '')    // '1' | '0' | ''
  const [gearFilter,   setGear]     = useSessionState('user-list:gear', '')      // 'BOTH' | 'AUTOMATIC' | 'MANUAL' | ''
  const [vehicleFilter,setVehicle]  = useSessionState('user-list:vehicle', '')   // category id string
  const [schoolFilter, setSchool]   = useSessionState('user-list:school', '')    // vendor id string
  const [cityFilter,   setCity]     = useSessionState('user-list:city', '')      // city id string
  const [verifiedFilter,setVerified]= useSessionState('user-list:verified', '')  // '1' | '0' | ''
  const [handledByFilter,setHandledBy]= useSessionState('user-list:handledBy', '') // handler name (client-side)
  // Panel starts expanded so every available filter is visible on first load;
  // the collapse choice then persists across a list -> details -> back trip.
  const [filterOpen,   setFilterOpen] = useSessionState('user-list:filtersOpen', true)
  // Read once on mount: the card to return to, written by openUser before navigating.
  const [focusUserId] = useState<string | null>(() => {
    try { return sessionStorage.getItem(FOCUS_KEY) } catch { return null }
  })
  const focusDone = useRef(false)
  const [page,    setPage]    = useSessionState('user-list:page', 1)
  const [perPage, setPerPage] = useSessionState('user-list:perPage', 50)

  const debouncedSearch = useDebounce(search)

  // ── Reference data for filter dropdowns ──────────────────────────────────
  const { data: gearEnumData }   = useGearEnum()
  const gears: any[] = gearEnumData?.values ?? []

  // Uses the admin city list (not the public one, which silently drops any
  // city with is_published=false) so the filter dropdown shows every city,
  // not just the ones live on the public-facing app.
  const { data: citiesData } = useQuery({
    queryKey: ['cities-filter'],
    queryFn: () => restClient.get(`${endpoints.city.list}?limit=1000&locale=en`).then(r => r.data),
    staleTime: Infinity,
  })
  const cities: any[] = citiesData?.data ?? []

  const { data: categoriesData } = useQuery({
    queryKey: ['categories-filter'],
    queryFn: () => restClient.get(`${endpoints.users.listCategory}?page=1&limit=1000`).then(r => r.data),
    staleTime: Infinity,
  })
  const categories: any[] = categoriesData?.data ?? []

  const { data: schoolsData } = useQuery({
    queryKey: ['schools-filter'],
    queryFn: () => schoolService.list({ limit: 500 }),
    staleTime: 60_000,
    enabled: !isSchool,
  })
  const schools: any[] = schoolsData?.data ?? []

  const isDeletedTab = tab === 'DELETED'
  const isAllTab = tab === 'ALL'

  // ── Users query — mirrors old useGetUsers params exactly (disabled on the Deleted tab) ─────
  const { data: usersData, isLoading: usersLoading, isError: usersError } = useUsers({
    page:          page,
    limit:         perPage,
    search:        debouncedSearch || undefined,
    user_types:    (isDeletedTab || isAllTab) ? undefined : tab,
    is_active:     statusFilter !== '' ? Number(statusFilter) : undefined,
    is_verified:   verifiedFilter !== '' ? Number(verifiedFilter) : undefined,
    vendor_id:     schoolFilter ? Number(schoolFilter) : undefined,
    city_id:       cityFilter     ? Number(cityFilter)    : undefined,
    vehicle_type_id: vehicleFilter ? Number(vehicleFilter) : undefined,
    // gear: numeric value mapped from name, same as old gearTypeMapping
    gear:          gearFilter ? String(GEAR_TYPE_MAP[gearFilter] ?? '') : undefined,
    // In school mode the dedicated get-school-trainer-list endpoint is used instead.
  }, { enabled: !isDeletedTab && !isSchool })

  // ── Deleted users — dedicated tracking endpoint ───────────────────────────
  const { data: deletedData, isLoading: deletedLoading, isError: deletedError } = useQuery({
    queryKey: ['deleted-users', page, perPage, debouncedSearch],
    queryFn: () => {
      const p = new URLSearchParams({ page: String(page), limit: String(perPage) })
      if (debouncedSearch) p.append('search', debouncedSearch)
      return restClient.get(`${endpoints.users.deletedList}?${p.toString()}`).then(r => r.data)
    },
    enabled: isDeletedTab,
  })

  // ── School admin — dedicated own-school trainer list (get-school-trainer-list).
  // Scoped server-side to the logged-in school admin's school via the auth token.
  const { data: schoolTrainersData, isLoading: schoolTrainersLoading, isError: schoolTrainersError } = useQuery({
    queryKey: ['school-trainers', page, perPage, debouncedSearch],
    queryFn: () => schoolService.schoolTrainers({
      page,
      limit: perPage,
      search: debouncedSearch || undefined,
    }),
    enabled: isSchool,
  })

  const data      = isSchool ? schoolTrainersData : isDeletedTab ? deletedData : usersData
  const isLoading = isSchool ? schoolTrainersLoading : isDeletedTab ? deletedLoading : usersLoading
  const isError   = isSchool ? schoolTrainersError : isDeletedTab ? deletedError : usersError

  // When we're returning from a details page we scroll to that user's card instead,
  // so the generic scrollTop restore is skipped to keep the two from fighting.
  useScrollRestoration('user-list', !isLoading && !focusUserId)

  const deleteUser = useDeleteUser()
  const qc = useQueryClient()
  // tracks the row + action currently in flight
  const [busy, setBusy] = useState<{ id: number; type: 'delete' | 'verify' | 'restore' } | null>(null)
  // user pending delete confirmation (null = dialog closed)
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null)
  // user pending restore confirmation (null = dialog closed)
  const [restoreTarget, setRestoreTarget] = useState<any | null>(null)
  // "Handled by" has no backend param, so on the Students tab the full result set (every
  // page, with the other filters applied server-side) is fetched once; it supplies the
  // dropdown options and is filtered + paginated locally while a handler is selected.
  const studentParams = {
    search:      debouncedSearch || undefined,
    user_types:  'STUDENT',
    is_active:   statusFilter !== '' ? Number(statusFilter) : undefined,
    is_verified: verifiedFilter !== '' ? Number(verifiedFilter) : undefined,
    vendor_id:   schoolFilter ? Number(schoolFilter) : undefined,
    city_id:     cityFilter ? Number(cityFilter) : undefined,
  }
  const { data: fullStudents = [], isLoading: fullLoading, isError: fullError } = useQuery({
    queryKey: ['students-all', studentParams],
    queryFn: async () => {
      const limit = 500
      const out: any[] = []
      for (let pg = 1; pg <= 50; pg++) {
        const res: any = await userService.list({ ...studentParams, page: pg, limit })
        const batch: any[] = res?.data ?? []
        out.push(...batch)
        const tot = res?.total ?? res?.meta?.total ?? 0
        if (batch.length === 0 || out.length >= tot) break
      }
      return out
    },
    enabled: tab === 'STUDENT' && !isSchool,
    staleTime: 30_000,
  })
  const handledByActive = tab === 'STUDENT' && !isSchool && !!handledByFilter
  const handledRows: any[] = handledByActive
    ? fullStudents.filter((r: any) => Array.isArray(r.handled_by) && r.handled_by.some((h: any) => h.name === handledByFilter))
    : []
  const handlerNames: string[] = Array.from(new Set<string>(
    fullStudents.flatMap((r: any) => Array.isArray(r.handled_by) ? r.handled_by.map((h: any) => h.name).filter(Boolean) : [])
  )).sort((a, b) => a.localeCompare(b))

  const rawRows   = handledByActive
    ? handledRows.slice((page - 1) * perPage, page * perPage)
    : (data as any)?.data ?? []
  // get-school-trainer-list returns trainer-mapping rows with identity nested under
  // `user` and stats under different field names. Flatten/remap so TrainerCard
  // (which reads the flat admin-list shape) renders the same.
  const rows: any[] = isSchool ? rawRows.map(mapSchoolTrainerRow) : rawRows
  const total     = handledByActive ? handledRows.length : (data as any)?.total ?? (data as any)?.meta?.total ?? 0
  const lastPage  = Math.ceil(total / perPage) || 1

  // Back from a details page: scroll that user's card into view and flash a ring on it
  // so it's obvious where in the list they left off. Runs once, after the rows land.
  useEffect(() => {
    if (isLoading || !focusUserId || focusDone.current) return
    focusDone.current = true
    try { sessionStorage.removeItem(FOCUS_KEY) } catch {}
    const idx = rows.findIndex((r: any) => String(r.id) === focusUserId)
    if (idx < 0) return
    requestAnimationFrame(() => {
      const el = document.getElementById('user-list-grid')?.children[idx] as HTMLElement | undefined
      if (!el) return
      el.scrollIntoView({ block: 'center' })
      el.classList.add('ring-2', 'ring-brand-500')
      setTimeout(() => el.classList.remove('ring-2', 'ring-brand-500'), 2000)
    })
  }, [isLoading, rows, focusUserId])

  // Filters only affect the admin user list — the deleted-users and school-trainer
  // endpoints accept search only, so the panel is hidden there rather than shown inert.
  const hasFilters = !isSchool && !isDeletedTab

  // active filter count for the toggle badge
  const activeFilterCount = [statusFilter, gearFilter, vehicleFilter, schoolFilter, cityFilter, verifiedFilter, tab === 'STUDENT' && handledByFilter].filter(Boolean).length

  // one descriptor per applied filter, driving the chip row
  const chips = ([
    statusFilter && {
      key: 'status', label: t('userList.labelStatus'),
      value: statusFilter === '1' ? t('common.active') : t('common.inactive'),
      clear: () => { setStatus(''); setPage(1) },
    },
    vehicleFilter && {
      key: 'vehicle', label: t('userList.vehicleType'),
      value: pickTranslation(categories.find((c: any) => String(c.id) === vehicleFilter)?.category_translations, i18n.language)?.name ?? vehicleFilter,
      clear: () => { setVehicle(''); setPage(1) },
    },
    gearFilter && {
      key: 'gear', label: t('userList.gearType'), value: gearFilter,
      clear: () => { setGear(''); setPage(1) },
    },
    schoolFilter && {
      key: 'school', label: t('userList.labelSchool'),
      value: pickTranslation(schools.find((s: any) => String(s.id) === schoolFilter)?.vendor_translations, i18n.language)?.name ?? schoolFilter,
      clear: () => { setSchool(''); setPage(1) },
    },
    cityFilter && {
      key: 'city', label: t('userList.labelCity'),
      value: pickTranslation(cities.find((c: any) => String(c.id) === cityFilter)?.city_translations, i18n.language)?.name ?? cityFilter,
      clear: () => { setCity(''); setPage(1) },
    },
    tab === 'STUDENT' && handledByFilter && {
      key: 'handledBy', label: t('userList.handledBy'), value: handledByFilter,
      clear: () => { setHandledBy(''); setPage(1) },
    },
    verifiedFilter && {
      key: 'verified', label: t('userList.verification'),
      value: verifiedFilter === '1' ? t('userList.verified') : t('userList.unverified'),
      clear: () => { setVerified(''); setPage(1) },
    },
  ].filter(Boolean) as { key: string; label: string; value: string; clear: () => void }[])

  const resetFilters = () => {
    setStatus(''); setGear(''); setVehicle(''); setSchool(''); setCity(''); setVerified(''); setHandledBy('')
    setPage(1)
  }

  // Performs the delete once confirmed via the modal.
  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return
    const id = deleteTarget.id
    setBusy({ id, type: 'delete' })
    try {
      await deleteUser.mutateAsync(id)
      toast.success(t('userList.toastDeleted', { name: deleteTarget.name ?? t('userList.defaultUser') }))
      setDeleteTarget(null)
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('userList.toastDeleteFail'))
    } finally {
      setBusy(null)
    }
  }, [deleteUser, deleteTarget])

  // Restores a deleted user — POST admin/user/tracking/{id}/restore
  const confirmRestore = useCallback(async () => {
    if (!restoreTarget) return
    const id = restoreTarget.id
    setBusy({ id, type: 'restore' })
    try {
      await restClient.post(`${endpoints.users.tracking}/${id}/restore`, {})
      toast.success(t('userList.toastRestored', { name: restoreTarget.name ?? t('userList.defaultUser') }))
      setRestoreTarget(null)
      await qc.invalidateQueries({ queryKey: ['deleted-users'] })
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('userList.toastRestoreFail'))
    } finally {
      setBusy(null)
    }
  }, [restoreTarget, qc])

  // Verify (verify=true) / unverify (verify=false) a trainer.
  // Uses the trainerPendingRequest status endpoint with the trainer's mapping id.
  const handleToggleVerify = useCallback(async (user: any, verify: boolean) => {
    const mappingId = user.vendor_trainer_mapping_id ?? user.mapping_id ?? user.id
    setBusy({ id: user.id, type: 'verify' })
    try {
      await restClient.post(endpoints.pendingRequest.rejectAccept, { mapping_id: mappingId, verify })
      await qc.invalidateQueries({ queryKey: ['users'] })
      toast.success(verify ? t('userList.toastVerified') : t('userList.toastUnverified'))
    } catch (e: any) {
      const errs = e?.response?.data?.errors
      const msg = errs
        ? (typeof errs === 'string' ? errs : Object.values(errs).flat().join(', '))
        : (e?.response?.data?.message ?? t('userList.toastVerifyFail'))
      toast.error(msg)
    } finally {
      setBusy(null)
    }
  }, [qc])

  // Remembers the card before leaving so the list can return to it.
  const openUser = useCallback((u: any) => {
    try { sessionStorage.setItem(FOCUS_KEY, String(u.id)) } catch {}
    navigate(isSchool ? `/school/trainer/${u.id}` : `/dashboard/user/${u.id}`)
  }, [navigate, isSchool])

  const handleTabChange = (newTab: TabValue) => {
    setTab(newTab)
    setPage(1)
    setSearch(''); setStatus(''); setGear(''); setVehicle(''); setSchool(''); setCity(''); setVerified('')
    setNotifyMode(false); setNotifyIds([])
  }

  // Deep link from the dashboard (e.g. Total Students card) picks the tab on arrival.
  useEffect(() => {
    const wanted = (location.state as { tab?: TabValue } | null)?.tab
    if (wanted && !isSchool && wanted !== storedTab) handleTabChange(wanted)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isTrainerTab = tab === 'TRAINER'
  const isStudentTab = tab === 'STUDENT'
  const tabLabel = t(`userList.tabs.${tab}`)

  // "Select trainers → send notification" mode — only on the admin-facing
  // Trainers tab; a school's own trainer list and the Deleted tab skip it.
  const canNotify = isTrainerTab && !isSchool && !isDeletedTab
  const [notifyMode, setNotifyMode] = useState(false)
  const [notifyIds, setNotifyIds] = useState<number[]>([])
  const [notifyModalOpen, setNotifyModalOpen] = useState(false)
  const toggleNotifyId = (id: number) => setNotifyIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const exitNotifyMode = () => { setNotifyMode(false); setNotifyIds([]) }
  const notifyRecipients: NotificationRecipient[] = notifyIds
    .map(id => rows.find((r: any) => r.id === id))
    .filter(Boolean)
    .map((r: any) => ({ id: r.id, name: localized(r, 'name', i18n.language) || `#${r.id}` }))

  return (
    <div className="space-y-5">

      {/* ── Page Header ── */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1.5">
            <span>{t('nav.dashboard')}</span>
            <ChevronRight size={12} className="rtl:rotate-180"/>
            <span>{t('nav.users')}</span>
            <ChevronRight size={12} className="rtl:rotate-180"/>
            <span className="text-brand-500 font-medium">{tabLabel}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900 truncate">{t('userList.pageTitle', { type: tabLabel })}</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {isLoading ? t('userList.loadingCount') : t('userList.totalCount', { total: total.toLocaleString(), type: tabLabel })}
          </p>
        </div>

        {/* ── Primary action(s) — fixed position, never pushed around by filters ── */}
        {canNotify && notifyMode ? (
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs font-semibold text-gray-500">{notifyIds.length} selected</span>
            <button
              onClick={() => setNotifyModalOpen(true)}
              disabled={notifyIds.length === 0}
              className="flex items-center gap-1.5 h-10 px-4 text-sm font-bold rounded-xl text-white shadow-sm transition-all hover:opacity-90 disabled:opacity-40 disabled:hover:opacity-40"
              style={{ background: '#f68b2c' }}
            >
              <Send size={15}/> Send Notification
            </button>
            <button onClick={exitNotifyMode}
              className="flex items-center gap-1 h-10 px-3 text-sm font-semibold rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50">
              <X size={14}/> Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-shrink-0">
            {canNotify && (
              <button
                onClick={() => setNotifyMode(true)}
                className="flex items-center gap-1.5 h-10 px-4 text-sm font-bold rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <Send size={15}/> Notify Trainers
              </button>
            )}
            <Can perm="POST /admin/auth/register">
            <button
              onClick={() => navigate(isSchool ? '/school/new-trainer' : '/dashboard/user/new')}
              className="flex items-center gap-1.5 h-10 px-4 text-sm font-bold rounded-xl text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-brand-400/50 focus:ring-offset-2"
              style={{ background: '#f68b2c' }}
            >
              <Plus size={16}/> {t('userList.createNew', { type: isTrainerTab ? t('userList.tabs.TRAINER') : tabLabel })}
            </button>
            </Can>
          </div>
        )}
      </div>

      {/* ── Tabs ── (hidden for school admins — they only ever see their own trainers) */}
      {!isSchool && (
        <div className="-mx-1 px-1 overflow-x-auto pb-0.5">
          <div className="flex items-center gap-2 w-max min-w-full">
            {USER_TABS.map(tb => {
              const isActive = tab === tb.value
              const count = isActive ? total : undefined
              return (
                <button
                  key={tb.value}
                  onClick={() => handleTabChange(tb.value as TabValue)}
                  className={cn(
                    'flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-semibold border whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/40',
                    isActive
                      ? 'text-white border-transparent'
                      : 'bg-white border-gray-200 text-gray-600'
                  )}
                  style={isActive ? { background: '#f68b2c', borderColor: '#f68b2c' } : {}}
                >
                  {t(`userList.tabs.${tb.value}`)}
                  {count !== undefined && (
                    <span className="text-xs font-bold rounded-full px-1.5 py-0.5 bg-white/25 tabular-nums">
                      {count.toLocaleString()}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Toolbar: search + filters ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2 p-3 sm:p-4">

          {/* ── Search ── */}
          <div className="relative flex-1 min-w-0">
            <Search size={15} className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder={t('userList.searchPlaceholder')}
              aria-label={t('userList.searchPlaceholder')}
              className="w-full h-10 ps-10 pe-9 text-sm text-gray-700 placeholder-gray-400 bg-gray-50/70 border border-gray-200 rounded-xl transition-colors hover:border-gray-300 focus:outline-none focus:bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); setPage(1) }}
                aria-label={t('userList.clearSearch')}
                className="absolute end-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X size={13}/>
              </button>
            )}
          </div>

          {/* ── Filters toggle (admin only — school admins keep only search) ── */}
          {hasFilters && (
            <button
              onClick={() => setFilterOpen(o => !o)}
              aria-expanded={filterOpen}
              className={cn(
                'flex items-center gap-2 h-10 px-3.5 text-sm font-semibold rounded-xl border transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-brand-400/30',
                filterOpen || activeFilterCount > 0
                  ? 'border-brand-300 bg-brand-50 text-brand-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
              )}
            >
              <SlidersHorizontal size={15}/>
              <span className="hidden sm:inline">{t('userList.filters')}</span>
              {activeFilterCount > 0 && (
                <span className="min-w-5 h-5 px-1 flex items-center justify-center rounded-full bg-brand-500 text-white text-[11px] font-bold tabular-nums">
                  {activeFilterCount}
                </span>
              )}
              <ChevronDown size={14} className={cn('transition-transform text-gray-400', filterOpen && 'rotate-180')}/>
            </button>
          )}
        </div>

        {/* ── Filter panel ── */}
        {hasFilters && filterOpen && (
          <div className="border-t border-gray-100 p-3 sm:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">

              {/* Status */}
              <FilterSelect label={t('userList.labelStatus')} value={statusFilter} onChange={v => { setStatus(v); setPage(1) }}>
                <option value="">{t('userList.allStatus')}</option>
                <option value="1">{t('common.active')}</option>
                <option value="0">{t('common.inactive')}</option>
              </FilterSelect>

              {/* ── Trainer-only filters ── */}
              {isTrainerTab && (
                <>
                  {/* Vehicle / Category */}
                  <FilterSelect label={t('userList.vehicleType')} value={vehicleFilter} onChange={v => { setVehicle(v); setPage(1) }}>
                    <option value="">{t('userList.allVehicles')}</option>
                    {categories.map((c: any) => {
                      const name = pickTranslation(c.category_translations, i18n.language)?.name ?? t('userList.unknown')
                      return <option key={c.id} value={c.id}>{name}</option>
                    })}
                  </FilterSelect>

                  {/* Gear */}
                  <FilterSelect label={t('userList.gearType')} value={gearFilter} onChange={v => { setGear(v); setPage(1) }}>
                    <option value="">{t('userList.allGears')}</option>
                    {gears.map((g: any) => (
                      <option key={g.value} value={g.name}>{g.name}</option>
                    ))}
                  </FilterSelect>
                </>
              )}

              {/* ── Trainer + student filters ── */}
              {(isTrainerTab || isStudentTab) && (
                <>
                  {/* School */}
                  <FilterSelect label={t('userList.labelSchool')} value={schoolFilter} onChange={v => { setSchool(v); setPage(1) }}>
                    <option value="">{t('userList.allSchools')}</option>
                    {schools.map((s: any) => {
                      const tr = pickTranslation(s.vendor_translations, i18n.language)
                      return <option key={s.id} value={s.id}>{tr?.name ?? t('userList.unknown')}</option>
                    })}
                  </FilterSelect>

                  {/* City */}
                  <FilterSelect label={t('userList.labelCity')} value={cityFilter} onChange={v => { setCity(v); setPage(1) }}>
                    <option value="">{t('userList.allCities')}</option>
                    {cities.map((c: any) => {
                      const tr = pickTranslation(c.city_translations, i18n.language)
                      return <option key={c.id} value={c.id}>{tr?.name ?? t('userList.unknown')}</option>
                    })}
                  </FilterSelect>

                  {/* Verification */}
                  <FilterSelect label={t('userList.verification')} value={verifiedFilter} onChange={v => { setVerified(v); setPage(1) }}>
                    <option value="">{t('userList.allVerification')}</option>
                    <option value="1">{t('userList.verified')}</option>
                    <option value="0">{t('userList.unverified')}</option>
                  </FilterSelect>
                </>
              )}

              {/* Handled by — students only */}
              {isStudentTab && (
                <FilterSelect label={t('userList.handledBy')} value={handledByFilter} onChange={v => { setHandledBy(v); setPage(1) }}>
                  <option value="">{t('userList.allHandlers')}</option>
                  {handlerNames.map(n => <option key={n} value={n}>{n}</option>)}
                  {handledByFilter && !handlerNames.includes(handledByFilter) && (
                    <option value={handledByFilter}>{handledByFilter}</option>
                  )}
                </FilterSelect>
              )}
            </div>
          </div>
        )}

        {/* ── Active filter chips ── */}
        {chips.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap border-t border-gray-100 px-3 sm:px-4 py-2.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 me-0.5">{t('userList.activeFilters')}</span>
            {chips.map(c => (
              <FilterChip key={c.key} label={c.label} value={c.value} onRemove={c.clear} removeLabel={t('userList.removeFilter', { name: c.label })}/>
            ))}
            <button
              onClick={resetFilters}
              className="ms-auto flex items-center gap-1 h-7 px-2.5 text-xs font-semibold rounded-full text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
            >
              <RotateCcw size={11}/> {t('userList.clearAll')}
            </button>
          </div>
        )}
      </div>

      {/* ── Grid of Cards ── */}
      {(isLoading || (handledByActive && fullLoading)) ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-96 bg-gray-100 rounded-2xl animate-pulse"/>
          ))}
        </div>
      ) : (isError || (handledByActive && fullError)) ? (
        <div className="bg-white rounded-2xl border border-red-100 p-8 text-center">
          <p className="text-red-500 text-sm">{t('userList.loadError')}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <p className="text-gray-400 text-sm">{t('userList.emptyState', { type: tabLabel })}</p>
          {(activeFilterCount > 0 || search) && (
            <button
              onClick={() => { resetFilters(); setSearch('') }}
              className="mt-3 text-xs font-semibold text-brand-600 hover:text-brand-700 underline underline-offset-2"
            >{t('userList.clearFilters')}</button>
          )}
        </div>
      ) : (
        <div id="user-list-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((u: any) => (
            isTrainerTab ? (
              <TrainerCard
                key={u.id}
                user={u}
                isSchool={isSchool}
                onView={() => openUser(u)}
                onDelete={() => setDeleteTarget(u)}
                onToggleVerify={(verify) => handleToggleVerify(u, verify)}
                onRestore={() => setRestoreTarget(u)}
                deleted={isDeletedTab}
                restoring={busy?.id === u.id && busy?.type === 'restore'}
                deleting={busy?.id === u.id && busy?.type === 'delete'}
                verifying={busy?.id === u.id && busy?.type === 'verify'}
                selectable={notifyMode}
                selected={notifyIds.includes(u.id)}
                onToggleSelect={() => toggleNotifyId(u.id)}
              />
            ) : (
              <UserCard
                key={u.id}
                user={u}
                onView={() => openUser(u)}
                onDelete={() => setDeleteTarget(u)}
                onRestore={() => setRestoreTarget(u)}
                deleted={isDeletedTab}
                showType={isAllTab || isDeletedTab}
                restoring={busy?.id === u.id && busy?.type === 'restore'}
                deleting={busy?.id === u.id && busy?.type === 'delete'}
              />
            )
          ))}
        </div>
      )}

      {/* ── Pagination ── */}
      {total > 0 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 px-5 py-3 flex-wrap gap-3">
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>{t('common.pagination.rowsPerPage')}</span>
            <select
              value={perPage}
              onChange={e => { setPerPage(Number(e.target.value)); setPage(1) }}
              className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              {[10, 15, 20, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>
              {t('userList.range', { start: ((page - 1) * perPage) + 1, end: Math.min(page * perPage, total), total: total.toLocaleString() })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page <= 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-gray-600 font-medium"
            >
              <ChevronLeft size={14} className="rtl:rotate-180"/> {t('common.pagination.previous')}
            </button>
            {Array.from({ length: Math.min(5, lastPage) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, lastPage - 4)) + i
              return (
                <button key={p}
                  onClick={() => setPage(p)}
                  className={cn('w-8 h-8 text-sm rounded-lg border font-medium',
                    p === page ? 'bg-brand-500 text-white border-brand-500' : 'border-gray-200 hover:bg-gray-50 text-gray-600'
                  )}
                >{p}</button>
              )
            })}
            <span className="text-sm text-gray-400">{t('userList.ofPages', { n: lastPage })}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= lastPage}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-gray-600 font-medium"
            >
              {t('common.pagination.next')} <ChevronRight size={14} className="rtl:rotate-180"/>
            </button>
          </div>
        </div>
      )}

      {/* ── Delete confirmation modal ── */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={t('userList.deleteDialogTitle')}
        description={t('userList.deleteDialogDesc', { name: deleteTarget?.name ?? t('userList.thisUser') })}
        confirmLabel={t('userList.deleteConfirm')}
        variant="danger"
        loading={busy?.type === 'delete'}
      />

      {/* ── Restore confirmation modal ── */}
      <ConfirmDialog
        open={!!restoreTarget}
        onClose={() => setRestoreTarget(null)}
        onConfirm={confirmRestore}
        title={t('userList.restoreDialogTitle')}
        description={t('userList.restoreDialogDesc', { name: restoreTarget?.name ?? t('userList.thisUser') })}
        confirmLabel={t('userList.restoreConfirm')}
        variant="primary"
        loading={busy?.type === 'restore'}
      />

      {/* ── Send notification modal (trainers selected via Notify mode above) ── */}
      <SendNotificationModal
        open={notifyModalOpen}
        onClose={() => setNotifyModalOpen(false)}
        recipients={notifyRecipients}
        onRemoveRecipient={toggleNotifyId}
        userType="TRAINER"
        onSuccess={() => { setNotifyModalOpen(false); exitNotifyMode() }}
      />
    </div>
  )
}
